import { Link, useLocation, useRouterState } from "@tanstack/react-router";
import { ArrowRight, BookOpen, BriefcaseBusiness, FileCheck2 } from "lucide-react";
import type { Locale } from "@/lib/i18n";
import { quotePath, quoteSearch } from "@/lib/quote-links";
import {
  resolveResourceCommercialContext,
  type ResourceTaxonomyInput,
} from "@/lib/resource-commerce";

interface SystemBridgeCalloutProps {
  locale: Locale;
}

function useLoadedResource(): ResourceTaxonomyInput | undefined {
  return useRouterState({
    select: (state) => {
      for (let index = state.matches.length - 1; index >= 0; index -= 1) {
        const loaderData = state.matches[index]?.loaderData as
          | { resource?: ResourceTaxonomyInput }
          | undefined;
        if (loaderData?.resource) return loaderData.resource;
      }
      return undefined;
    },
  });
}

export function SystemBridgeCallout({ locale }: SystemBridgeCalloutProps) {
  const location = useLocation();
  const loadedResource = useLoadedResource();
  const isEs = locale === "es";
  const context = resolveResourceCommercialContext({
    locale,
    pathname: location.pathname,
    resource: {
      ...loadedResource,
      slug: loadedResource?.slug ?? location.pathname.split("/").filter(Boolean).at(-1),
    },
  });

  const copy = isEs
    ? {
        eyebrow: "Ruta recomendada",
        title: "Del aprendizaje a una decisión ejecutable.",
        body: `Este recurso pertenece al recorrido de ${context.offerLabel}. Usa la guía pilar para el marco completo, revisa la capacidad relevante y solicita un scope cuando el problema ya requiere implementación.`,
        pillar: "Leer la guía pilar",
        service: "Ver la capacidad relevante",
      }
    : {
        eyebrow: "Recommended path",
        title: "From learning to an executable decision.",
        body: `This resource belongs to the ${context.offerLabel} journey. Use the pillar guide for the complete framework, review the relevant capability, and request a scope when the issue requires implementation.`,
        pillar: "Read the pillar guide",
        service: "Review the relevant capability",
      };

  const cards = [
    {
      icon: BookOpen,
      label: copy.pillar,
      detail: context.pillarLabel,
      href: context.pillarHref,
    },
    {
      icon: BriefcaseBusiness,
      label: copy.service,
      detail: context.serviceLabel,
      href: context.serviceHref,
    },
  ];

  return (
    <section
      aria-label={copy.eyebrow}
      className="not-prose mt-16 rounded-3xl border border-[var(--line-neutral)] bg-[var(--surface-secondary)] p-7 sm:p-9"
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-foreground">
        {copy.eyebrow}
      </p>
      <h2 className="mt-4 font-display text-2xl font-semibold leading-tight tracking-tight text-foreground sm:text-3xl">
        {copy.title}
      </h2>
      <p className="mt-4 max-w-3xl text-sm leading-relaxed text-muted-foreground sm:text-base">
        {copy.body}
      </p>

      <div className="mt-7 grid gap-3 sm:grid-cols-2">
        {cards.map(({ icon: Icon, label, detail, href }) => (
          <Link
            key={href}
            to={href}
            className="group rounded-2xl border border-[var(--line-neutral)] bg-[var(--surface-editorial)] p-5 transition-colors hover:border-foreground/30"
          >
            <Icon className="h-5 w-5 text-foreground" />
            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {label}
            </p>
            <p className="mt-2 flex items-center gap-2 font-display text-lg font-semibold text-foreground">
              {detail}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </p>
          </Link>
        ))}
      </div>

      <div className="mt-4 rounded-2xl border border-[var(--line-neutral)] bg-[var(--surface-methodology)] p-5 sm:flex sm:items-center sm:justify-between sm:gap-6">
        <div>
          <FileCheck2 className="h-5 w-5 text-foreground" />
          <p className="mt-3 font-display text-lg font-semibold text-foreground">{context.offerLabel}</p>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{context.offerSummary}</p>
        </div>
        <Link
          to={quotePath(locale)}
          search={quoteSearch({
            source: context.source,
            intent: context.intent,
            challenge: context.challenge,
          })}
          className="mt-5 inline-flex shrink-0 items-center gap-2 rounded-full border border-black/20 bg-[var(--action-primary)] px-5 py-2.5 text-sm font-semibold text-[var(--action-primary-text)] transition hover:brightness-95 sm:mt-0"
        >
          {context.scopeLabel}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
}